/** Individual evergreen boughs; one botanical layout for seasons and LODs. */
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { range, type Rng } from "@hyperlight/shared";
import { V, tube, shade, paint, flat } from "./primitives";
import type { BuildCtx, C3, SpeciesParams } from "./types";
import { TREE_STEM, paintTreeWind, windJoint, type WindJoint } from './treeWind';

interface Tuft {
  rim: THREE.Vector3[];
  ridge: THREE.Vector3;
  underside: THREE.Vector3;
  tone: number;
  snow: number;
}
interface Spray { root: THREE.Vector3; tip: THREE.Vector3; tufts: Tuft[] }
interface Whorl { center: THREE.Vector3; radius: number; depth: number; phase: number; sprays: Spray[] }
export interface ConiferPlan {
  height: number;
  radius: number;
  top: THREE.Vector3;
  whorls: Whorl[];
}

/** Pigments and season never consume shape randomness. All branches attach to
 * the same leaning stem. Overlapping tufts hide the construction rings. */
export function planConifer(rng: Rng, sp: SpeciesParams, maturity: number): ConiferPlan {
  const grow = .45 + .75 * maturity, girth = .65 + .6 * maturity;
  const height = range(rng, ...sp.trunk.height) * grow, radius = sp.trunk.radius * girth;
  const top = V(range(rng, -1, 1) * sp.trunk.lean * height, height,
    range(rng, -1, 1) * sp.trunk.lean * height);
  const spruce = sp.canopy.style === "droopTiers";
  const profile = sp.conifer ?? { droop: spruce ? .24 : .07, spread: 1, fullness: spruce ? .85 : .65 };
  const crownBase = sp.conifer?.crownBase ?? sp.trunk.forkHeight;
  const count = Math.min(6, Math.max(5, Math.round(range(rng, ...sp.canopy.count) * (.8 + .4 * maturity))));
  const crownWidth = range(rng, ...sp.canopy.size) * girth * 1.2 * profile.spread;
  // Wider, open crowns and narrower spires coexist within each species.
  const taper = profile.fullness * range(rng, .85, 1.2), phase = rng() * Math.PI * 2;
  const branchCount = count * 4 - 1;
  const steps = Array.from({ length: branchCount - 1 }, () => range(rng, .5, 1.5));
  const total = steps.reduce((a, b) => a + b, 0);
  let cursor = 0, branchIndex = 0;
  const whorls: Whorl[] = [];
  for (let t = 0; t < count; t++) {
    const sprays: Spray[] = [];
    const branches = t === count - 1 ? 3 : 4;
    const depth = height * (1 - crownBase) / count;
    for (let j = 0; j < branches; j++, branchIndex++) {
      if (branchIndex > 0) cursor += steps[branchIndex - 1];
      const f = cursor / total;
      const root = top.clone().multiplyScalar(crownBase + (.995 - crownBase) * f);
      const angle = phase + branchIndex * 2.399963 + range(rng, -.38, .38);
      const along = V(Math.cos(angle), 0, Math.sin(angle)), across = V(-along.z, 0, along.x);
      const length = crownWidth * (.12 + .88 * Math.pow(1 - f, taper)) * range(rng, .8, 1.2);
      const tip = root.clone().addScaledVector(along, length * .72).add(V(0, -length * (profile.droop + .20), 0));
      const tufts: Tuft[] = [];
      // A small opaque core carries the mass; eighteen much smaller needle
      // sprays overlap its shoulders and fringe. Detail lives at branch scale,
      // rather than turning every bough into one oversized folded leaf.
      const drop = length * (profile.droop + .42);
      const at = (x: number, z: number, y: number) => root.clone().addScaledVector(along, x * length)
        .addScaledVector(across, z * length).add(V(0, y - x * drop, 0));
      const ridge = at(.38, 0, Math.max(length * .46, depth * .35));
      const width = range(rng, .64, .80);
      const coreRim = [at(-.13, 0, .05 * length), at(.42, -width, 0),
        at(1.02, 0, -.12 * length), at(.48, width * range(rng, .9, 1.12), 0)];
      tufts.push({ rim: coreRim, ridge, underside: at(.40, 0, 0), tone: range(rng, .84, .97), snow: .82 });
      for (let k = 0; k < 18; k++) {
        const sideSign = k % 2 ? 1 : -1, u = .12 + Math.floor(k / 2) * .10 + range(rng, -.035, .035);
        const edgeMid = coreRim[sideSign < 0 ? 1 : 3];
        const edge = u < .5 ? coreRim[0].clone().lerp(edgeMid, u * 2)
          : edgeMid.clone().lerp(coreRim[2], (u - .5) * 2);
        const origin = ridge.clone().lerp(edge, range(rng, .60, .96));
        const size = length * range(rng, .30, .46);
        const angle = sideSign * range(rng, .5, 1.1);
        const axis = along.clone().multiplyScalar(Math.cos(angle)).addScaledVector(across, Math.sin(angle));
        const side = V(-axis.z, 0, axis.x), fall = size * range(rng, .65, 1.0);
        const point = (x: number, z: number, y: number) => origin.clone().addScaledVector(axis, x * size)
          .addScaledVector(side, z * size).add(V(0, y - x * fall, 0));
        // Closed tetrahedral spray: three small lit facets, one underside.
        // The broad middle and staggered tips give a ragged feathered edge.
        const rim = [point(.38, -.40, 0), point(1, range(rng, -.08, .08), 0), point(.42, .40, 0)];
        tufts.push({ rim, ridge: point(0, 0, size * .24), underside: point(.6, 0, 0),
          tone: range(rng, .90, 1.13), snow: .8 });
      }
      sprays.push({ root, tip, tufts });
    }
    // Groups only aggregate the far silhouette. Near branches have independent
    // heights on a spiral, not a ring of branches on a horizontal plane.
    whorls.push({ center: sprays[1].root.clone(), radius: crownWidth, depth, phase, sprays });
  }
  return { height, radius, top, whorls };
}

class Surface {
  positions: number[] = [];
  colors: number[] = [];
  branches: number[] = [];
  branch: WindJoint = TREE_STEM;
  tri(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, color: C3): void {
    for (const p of [a, b, c]) {
      this.positions.push(...p.toArray()); this.colors.push(...color); this.branches.push(...this.branch);
    }
  }
  geometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.positions, 3));
    paint(g, [1, 1, 1], .7);
    g.setAttribute("color", new THREE.Float32BufferAttribute(this.colors, 3));
    paintTreeWind(g);
    g.setAttribute('branchWind', new THREE.Float32BufferAttribute(this.branches, 4));
    g.computeVertexNormals();
    return g;
  }
}

/** Snow shares the bough's upper facets and wind weight. Its inset rim leaves
 * dark needle tips exposed; the short edge wall meets the actual leaf surface. */
function snowCap(surface: Surface, spray: Tuft, color: C3, thickness: number): void {
  const support = spray.rim.map(p => spray.ridge.clone().lerp(p, spray.snow));
  const roof = support.map(p => p.clone().add(V(0, thickness, 0)));
  const ridge = spray.ridge.clone().add(V(0, thickness, 0));
  for (let i = 0; i < roof.length; i++) {
    const next = (i + 1) % roof.length;
    surface.tri(ridge, roof[next], roof[i], color);
    surface.tri(support[i], roof[i], support[next], shade(color, .9));
    surface.tri(support[next], roof[i], roof[next], shade(color, .9));
  }
}

/** Both branch ends terminate inside trunk/foliage; omit hidden cap faces. */
function branchTube(a: THREE.Vector3, b: THREE.Vector3, r0: number, r1: number, color: C3): THREE.BufferGeometry {
  const direction = b.clone().sub(a), length = direction.length();
  const g = flat(new THREE.CylinderGeometry(r1, r0, length, 3, 1, true));
  g.translate(0, length / 2, 0);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(V(0, 1, 0), direction.normalize()));
  g.translate(a.x, a.y, a.z);
  return paint(g, color);
}

export function buildConifer(rng: Rng, sp: SpeciesParams, ctx: BuildCtx, bark: C3, leaf: C3, snow: C3, far: boolean): THREE.BufferGeometry {
  const plan = planConifer(rng, sp, ctx.maturity), parts: THREE.BufferGeometry[] = [], surface = new Surface();
  const winter = ctx.season === "winter";
  parts.push(tube(V(0, 0, 0), plan.top, plan.radius, plan.radius * .035, bark, far ? 3 : 6));
  for (const [t, whorl] of plan.whorls.entries()) {
    if (far) {
      // One broken outline per whorl. Envelope comes from the detailed sprays;
      // eight rim points and two poles keep even winter trees below 150 tris.
      const box = new THREE.Box3();
      for (const s of whorl.sprays) for (const tuft of s.tufts)
        for (const p of [...tuft.rim, tuft.ridge, tuft.underside]) box.expandByPoint(p);
      const center = box.getCenter(V(0, 0, 0)), extent = box.getSize(V(0, 0, 0)).multiplyScalar(.5);
      const rim = Array.from({ length: 8 }, (_, i) => {
        const a = i * Math.PI / 4;
        const k = i % 2 ? .8 : 1;
        return V(center.x + Math.cos(a) * extent.x * k, box.min.y + extent.y * .25,
          center.z + Math.sin(a) * extent.z * k);
      });
      const peak = V(whorl.center.x, box.max.y + (winter ? .05 : 0), whorl.center.z);
      const bottom = V(center.x, box.min.y, center.z);
      for (let i = 0; i < 8; i++) {
        const next = (i + 1) % 8;
        surface.tri(peak, rim[next], rim[i], winter && i % 3 !== 0 ? snow : leaf);
        surface.tri(bottom, rim[i], rim[next], shade(leaf, .82));
      }
    } else {
      for (const spray of whorl.sprays) {
        const radius = plan.radius * (.30 - t * .02);
        // Short radial boughs lack the long limb + twig lever of broadleaf
        // crowns. Give their needle-bearing fans a visible response of their
        // own; wood, needles and snow still rotate about the same attachment.
        const response = sp.canopy.style === 'droopTiers' ? 5 : 4;
        surface.branch = windJoint(spray.root, spray.tip, radius, response);
        parts.push(paintTreeWind(branchTube(spray.root, spray.tip, radius, plan.radius * .025, bark), surface.branch));
        for (const [k, tuft] of spray.tufts.entries()) {
          for (let i = 0; i < tuft.rim.length; i++) {
            const next = (i + 1) % tuft.rim.length;
            surface.tri(tuft.ridge, tuft.rim[next], tuft.rim[i], shade(leaf, tuft.tone));
          }
          for (let i = 1; i < tuft.rim.length - 1; i++)
            surface.tri(tuft.rim[0], tuft.rim[i], tuft.rim[i + 1], shade(leaf, tuft.tone * .82));
          // Exposed upper sprays collect snow; most hanging tips stay green.
          if (winter && k === 0) snowCap(surface, tuft, snow, Math.min(.09, plan.height * .005));
        }
      }
    }
  }
  if (!far) for (let i = 0; i < 4; i++) {
    const a = i * Math.PI / 2;
    parts.push(tube(V(0, plan.radius * 1.8, 0), V(Math.cos(a) * plan.radius * 2.5, -.08, Math.sin(a) * plan.radius * 2.5), plan.radius * .45, plan.radius * .08, bark, 4));
  }
  parts.push(surface.geometry());
  for (const part of parts) if (!part.hasAttribute('branchWind')) paintTreeWind(part);
  const merged = mergeGeometries(parts, false);
  for (const part of parts) part.dispose();
  if (!merged) throw new Error(`buildConifer(${sp.id}): merge failed`);
  merged.computeVertexNormals();
  return merged;
}
